/**
 * useSignOut — unified sign-out path.
 *
 * Resets the RTK Query cache (preventing cross-school data leaks) and
 * calls `supabase.auth.signOut` with global scope. Every caller that
 * previously did `supabase.auth.signOut()` directly should route through
 * here so the cache reset is guaranteed.
 */
import { useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { resetAllApiCaches } from "../../../store";

interface UseSignOutResult {
  signOut: (scope?: "global" | "local" | "others") => Promise<void>;
}

export function useSignOut(): UseSignOutResult {
  const signOut = useCallback(
    async (scope: "global" | "local" | "others" = "global") => {
      resetAllApiCaches();
      // Don't throw on supabase errors — many callers fire this from
      // click handlers as `void signOut()` without a catch, so a reject
      // would surface as an unhandled promise rejection in production.
      // Log + swallow; the session listener in AuthContext still fires
      // on the server-side sign-out regardless of whether the client call
      // errored.
      try {
        const { error } = await supabase.auth.signOut({ scope });
        if (error) {
          console.error("[useSignOut] supabase signOut error:", error);
        }
      } catch (err) {
        console.error("[useSignOut] unexpected signOut exception:", err);
      }
    },
    [],
  );
  return { signOut };
}
